import * as React from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Alert } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { ApiError, getJSON, patchJSON } from '@/lib/api'
import type { EventTicketType } from './event-ticket-types'

type Destination = { id: string; name: string }
type MappingResponse = { revenue_destination_mappings?: { destinations?: Destination[] } }

export function EventRevenueDestinations({
  eventId,
  tickets,
  eventDestination,
}: {
  eventId: number
  tickets: EventTicketType[]
  eventDestination?: { id: string; name: string } | null
}) {
  const queryClient = useQueryClient()
  const query = useQuery({
    queryKey: ['revenue-destinations'],
    queryFn: () => getJSON<MappingResponse>('/api/v1/revenue_destination_mappings'),
    retry: false,
  })
  const update = useMutation({
    mutationFn: (values: Record<string, string | number | null>) =>
      patchJSON('/api/v1/revenue_destination_mappings', { revenue_destination_mappings: values }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event', eventId] })
    },
  })
  const destinations = query.data?.revenue_destination_mappings?.destinations ?? []
  const eventOverride = eventDestination
  const eventValue = eventOverride?.id ?? ''

  if (query.isLoading)
    return <p className="text-caption text-muted-foreground">Loading revenue destinations…</p>
  if (query.isError) {
    if (query.error instanceof ApiError && query.error.status === 404) return null
    return <Alert>Revenue Destinations could not be loaded.</Alert>
  }

  return (
    <section aria-labelledby="event-revenue-destinations-heading" className="space-y-2">
      <h2
        id="event-revenue-destinations-heading"
        className="text-caption font-medium text-muted-foreground"
      >
        Revenue Destinations
      </h2>
      <div className="divide-y divide-border rounded-md border border-border bg-card">
        <DestinationField
          label="Event"
          value={eventValue}
          inherited={!eventOverride}
          inheritedFrom={eventOverride ? undefined : inheritedSource(tickets)}
          destinations={destinations}
          disabled={update.isPending}
          onChange={(value) =>
            update.mutate({ event_id: eventId, event_destination_id: value || null })
          }
        />
        {tickets.map((ticket) => (
          <DestinationField
            key={ticket.id}
            label={ticket.name}
            value={
              ticket.revenue_destination_source === 'ticket_type_override'
                ? (ticket.revenue_destination?.id ?? '')
                : ''
            }
            inherited={ticket.revenue_destination_source !== 'ticket_type_override'}
            inheritedFrom={
              ticket.revenue_destination_source === 'event_override'
                ? 'Event'
                : 'Association default'
            }
            destinations={destinations}
            disabled={update.isPending}
            onChange={(value) =>
              update.mutate({
                event_ticket_type_id: ticket.id,
                ticket_type_destination_id: value || null,
              })
            }
          />
        ))}
      </div>
      {update.error && <Alert>Revenue Destination could not be saved.</Alert>}
    </section>
  )
}

function DestinationField({
  label,
  value,
  inherited,
  inheritedFrom,
  destinations,
  disabled,
  onChange,
}: {
  label: string
  value: string
  inherited: boolean
  inheritedFrom?: string
  destinations: Destination[]
  disabled: boolean
  onChange: (value: string) => void
}) {
  return (
    <div className="grid gap-2 px-4 py-3 sm:grid-cols-[148px_minmax(0,1fr)] sm:items-center">
      <p className="text-caption text-muted-foreground">{label}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Select
          value={value || 'inherited'}
          onValueChange={(next) => onChange(next === 'inherited' ? '' : next)}
          disabled={disabled}
        >
          <SelectTrigger className="h-8 min-w-56" aria-label={`${label} Revenue Destination`}>
            <SelectValue placeholder="No Revenue Destination" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="inherited">Inherit</SelectItem>
            {destinations.map((destination) => (
              <SelectItem key={destination.id} value={destination.id}>
                {destination.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-caption text-muted-foreground">
          {inherited
            ? `Inherited from ${inheritedFrom ?? 'Association default'}`
            : 'Overridden here'}
        </span>
        {!inherited && (
          <Button
            type="button"
            variant="ghost"
            className="h-7 px-2 text-caption"
            disabled={disabled}
            onClick={() => onChange('')}
          >
            Clear override
          </Button>
        )}
      </div>
    </div>
  )
}

function inheritedSource(tickets: EventTicketType[]) {
  const source = tickets.find(
    (ticket) => ticket.revenue_destination_source,
  )?.revenue_destination_source
  return source === 'association_default' ? 'Association default' : 'Association default'
}
